import * as React from 'react';
import { Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton, MessageBar, MessageBarType, Spinner, SpinnerSize } from '@fluentui/react';

export interface IResumeCaseDialogProps {
  isOpen: boolean;
  caseName: string;
  onConfirm: () => Promise<void>;
  onDismiss: () => void;
}

export const ResumeCaseDialog: React.FC<IResumeCaseDialogProps> = ({ isOpen, caseName, onConfirm, onDismiss }) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // The parent closes the dialog (isOpen -> false) on a successful confirm
  // without calling onDismiss, so reset transient state whenever it closes.
  React.useEffect(() => {
    if (!isOpen) {
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError((err as Error).message || 'Failed to resume case.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismiss = () => {
    setError(null);
    onDismiss();
  };

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={() => !isSubmitting && handleDismiss()}
      dialogContentProps={{
        type: DialogType.normal,
        title: 'Resume Case',
        subText: `Resuming "${caseName}" will re-enable all automated actions (emails, checks, attestation) from where the case was paused. Do you want to continue?`,
      }}
      modalProps={{ isBlocking: true }}
      minWidth={480}
    >
      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      <DialogFooter>
        {/* Keep both buttons mounted and focusable while submitting (disabled with
            allowDisabledFocus) so keyboard focus always has an actionable element
            inside the blocking dialog, per accessibility review. */}
        {isSubmitting && <Spinner size={SpinnerSize.small} label="Resuming..." />}
        <PrimaryButton
          text="Confirm Resume"
          onClick={handleConfirm}
          ariaLabel="Confirm resume case"
          disabled={isSubmitting}
          allowDisabledFocus
        />
        <DefaultButton
          text="Cancel"
          onClick={handleDismiss}
          ariaLabel="Cancel resume"
          disabled={isSubmitting}
          allowDisabledFocus
        />
      </DialogFooter>
    </Dialog>
  );
};
